import { DateTime } from "luxon";
import { useRef, useState, useLayoutEffect, useContext } from "react";

import { LiveUnitMetadataContext } from "@/common/contexts/LiveUnitMetadataContext";
import { setRequestDuration, setRequestStartTime } from "@/store/archives/slice";
import { RootState, useSelector, useDispatch } from "@/store/index";
import { ArchiveImagesNavMeta } from "@/utilities/types/archive";

import classes from "./VideoRequestOverlay.scss";


export const VideoRequestOverlay = () => {
    const videoSelectionWrapperRef = useRef<HTMLDivElement>(null);
    const endDeadzoneRef = useRef<HTMLDivElement>(null);
    const gridAndAccordionWidth: number = useSelector((state: RootState) => state.archives.gridAndAccordionWidth);
    const timelineWidth: number = useSelector((state: RootState) => state.archives.timelineWidth);
    const headerHeight: number = useSelector((state: RootState) => state.archives.dateTimeHeaderHeight);

    const unitMetadata = useContext<ArchiveImagesNavMeta>(LiveUnitMetadataContext);
    const timelineStart: DateTime = useSelector((state: RootState) => DateTime.fromISO(state.archives.timelineStart));
    const timeSpan: number = useSelector((state: RootState) => state.archives.timeSpan);
    const requestStartTime: DateTime = useSelector((state: RootState) =>
        DateTime.fromISO(state.archives.requestStartTime),
    );
    const requestDuration: number = useSelector((state: RootState) => state.archives.requestDuration);

    const [draggingRequestStart, setDraggingRequestStart] = useState<boolean>(false);
    const [draggingSelection, setDraggingSelection] = useState<boolean>(false);
    const [draggingRequestEnd, setDraggingRequestEnd] = useState<boolean>(false);

    const defaultRequestDuration = 4 * 60; // 4 min in seconds
    const defaultRequestStartTimeOffset = 30 * 60; //28 min in seconds previously. 30 mins now to match with input time on snapshop viewer

    const minimumDuration = 60; //seconds

    const dispatch = useDispatch();

    useLayoutEffect(() => {
        dispatch(setRequestStartTime(timelineStart.plus({ seconds: defaultRequestStartTimeOffset }).toISO()));
        dispatch(setRequestDuration(defaultRequestDuration));
    }, []);

    //General Purpose
    const pxPerSec = timelineWidth / timeSpan;

    const handleMouseUp = () => {
        setDraggingRequestStart(false);
        setDraggingSelection(false);
        setDraggingRequestEnd(false);
    };

    //For Start Time Drag
    const handleMouseDownRequestStart = () => {
        setDraggingRequestStart(true);
    };

    const handleMouseMoveRequestStart = (event: React.MouseEvent) => {
        //Only runs clamp logic if recording start value set in unitmeta
        if (
            requestStartTime.toSeconds() + event.movementX / pxPerSec > timelineStart.toSeconds() &&
            requestDuration - event.movementX / pxPerSec > minimumDuration
        ) {
            dispatch(setRequestStartTime(requestStartTime.plus({ seconds: event.movementX / pxPerSec }).toISO()));
            dispatch(setRequestDuration(requestDuration - event.movementX / pxPerSec));
        }

        if (requestDuration === minimumDuration && event.movementX < 0) {
            dispatch(setRequestStartTime(requestStartTime.plus({ seconds: event.movementX / pxPerSec }).toISO()));
            dispatch(setRequestDuration(requestDuration - event.movementX / pxPerSec));
        }
    };

    //For Selection Drag
    const handleMouseDownSelection = () => {
        setDraggingSelection(true);
    };

    const handleMouseMoveSelection = (event: React.MouseEvent) => {
        //Only runs clamp logic if recording start value set in unitmeta
        const requestStartTimeInSeconds = requestStartTime.toSeconds();

        if (
            requestStartTimeInSeconds + event.movementX / pxPerSec <
            timelineStart.plus({ hours: 1 }).toSeconds() - requestDuration &&
            requestStartTimeInSeconds + event.movementX / pxPerSec > timelineStart.toSeconds()
        ) {
            dispatch(setRequestStartTime(requestStartTime.plus({ seconds: event.movementX / pxPerSec }).toISO()));
        }
    };

    //For EndTime Drag
    const handleMouseDownRequestEnd = () => {
        setDraggingRequestEnd(true);
    };
    const handleMouseMoveRequestEnd = (event: React.MouseEvent) => {
        if (
            requestStartTime.toSeconds() + requestDuration + event.movementX / pxPerSec <
            timelineStart.plus({ hours: 1 }).toSeconds()
        ) {
            dispatch(setRequestDuration(requestDuration + event.movementX / pxPerSec));
        }
    };

    //Determines which drag logic to execute
    const handleWhichAspectShouldDrag = (event: React.MouseEvent) => {
        if (draggingRequestStart) {
            handleMouseMoveRequestStart(event);
        } else if (draggingSelection) {
            handleMouseMoveSelection(event);
        } else if (draggingRequestEnd) {
            handleMouseMoveRequestEnd(event);
        }
    };

    return (
        <>
            <div style={{ height: `calc(100% - ${headerHeight}px)` }} className={classes.videoRequestWrapper}>
                <div className={classes.videoRequestAccordionWrapper} style={{ width: gridAndAccordionWidth }}>
                    <div
                        className={classes.videoRequestTimelineWrapper}
                        style={{ width: timelineWidth }}
                        ref={videoSelectionWrapperRef}
                        onMouseLeave={handleMouseUp}
                        onMouseMove={handleWhichAspectShouldDrag}
                        onMouseUp={handleMouseUp}
                    >
                        <div
                            className={classes.priorToSelectionDeadZone}
                            style={{ width: requestStartTime.diff(timelineStart, "seconds").seconds * pxPerSec }}
                        >
                            <div
                                className={classes.handleBar}
                                draggable={false}
                                onMouseDown={handleMouseDownRequestStart}
                            >
                                {requestStartTime.setZone(unitMetadata.timezone).toLocaleString(DateTime.TIME_SIMPLE)}
                            </div>
                        </div>

                        <div
                            className={classes.videoRequestSelection}
                            style={{ width: requestDuration * pxPerSec }}
                            onMouseDown={handleMouseDownSelection}
                        />

                        <div
                            className={classes.afterSelectionDeadZone}
                            ref={endDeadzoneRef}
                            style={{
                                width:
                                    timelineStart
                                        .plus({ hours: 1 })
                                        .diff(requestStartTime, "seconds")
                                        .minus({ seconds: requestDuration }).seconds * pxPerSec,
                            }}
                        // Formula: timeline + 1hours worth of time - duration - first deadzone is this (request - timelinestart)
                        >
                            <div
                                className={classes.handleBar}
                                draggable={false}
                                onMouseDown={handleMouseDownRequestEnd}
                            >
                                {requestStartTime
                                    .setZone(unitMetadata.timezone)
                                    .plus({ minutes: requestDuration / 60 })
                                    .toLocaleString(DateTime.TIME_SIMPLE)}{" "}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
